import type { FiltersPayload } from '../GovernanceAnalyticsAiVisibility.utils';
import { SourceGrouping } from './citationSources.utils';
import { SourceHistoryBucket, SourceStatsTotal } from './GovernanceAnalyticsSources.utils';
import { IGovernancePromptMonitoringStatsService, OpenApi } from '@edgebox/sync-core-rest-client';
import { promptMonitoringAspect } from './promptMonitoringStatsRequests';
import { scheduleStatsRequest, StatsRequestPriority, statsRequestKey } from './statsRequestQueue';

/**
 * The ONLY place the Sources module talks to Sync Core.
 *
 * Both calls are made through the typed client wrapper, which serialises `includeSources`,
 * `groupBy`, `entityTermKey` and `sourceHosts` for us (the last two as CSV, from arrays). The
 * module above knows nothing about the transport; it only sees rows, or their absence.
 */

export interface SourceStatsQuery {
  filters: FiltersPayload;
  /** Registrable domain (the default reading) or full hostname. */
  groupBy: SourceGrouping;
  /** Restrict the figures to citations credited to this term. Empty means every entity. */
  entityTermKey?: string;
}

/** How the history endpoint buckets its windows — mirrors the Trends section's own switch. */
export type SourceHistoryGranularity = 'day' | 'week' | 'month';

/**
 * The slice of the client this module calls, described in the module's OWN reading of the answers.
 *
 * Narrow on purpose. The generated response types declare every per-source figure as required, while
 * this module renders a missing figure as absence rather than as a zero, so it reads them as optional
 * — and the real `ISyncCoreApi` satisfies this subset precisely because required is assignable to
 * optional. That is what lets the boundary be cast-free in both directions: production passes the
 * real client, and a test passes a plain object, neither needing `as unknown as ISyncCoreApi`.
 */
export interface SourceStatsApi {
  governance: {
    promptMonitoringStats: {
      total: (filters: SourceStatsRequestParameters) => Promise<SourceStatsTotal>;
      dailyHistory: (numberOfDays: number, filters: SourceStatsRequestParameters) => Promise<SourceHistoryBucket[]>;
      weeklyHistory: (numberOfWeeks: number, filters: SourceStatsRequestParameters) => Promise<SourceHistoryBucket[]>;
      monthlyHistory: (numberOfMonths: number, filters: SourceStatsRequestParameters) => Promise<SourceHistoryBucket[]>;
      /** Several of the routes above in one request; the statistics queue sends it for what waits together. */
      combined: IGovernancePromptMonitoringStatsService['combined'];
    };
  };
}

/** The history route, its aspect kind and its bucket count for each granularity, so the three always agree. */
const HISTORY_OF_GRANULARITY: Record<
  SourceHistoryGranularity,
  {
    method: 'dailyHistory' | 'weeklyHistory' | 'monthlyHistory';
    kind: OpenApi.GovernancePromptMonitoringStatsAspectKind;
    counts: (
      buckets: number
    ) => Pick<OpenApi.GovernancePromptMonitoringStatsAspectRequest, 'numberOfDays' | 'numberOfWeeks' | 'numberOfMonths'>;
  }
> = {
  day: {
    method: 'dailyHistory',
    kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.DailyHistory,
    counts: (buckets) => ({ numberOfDays: buckets }),
  },
  week: {
    method: 'weeklyHistory',
    kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.WeeklyHistory,
    counts: (buckets) => ({ numberOfWeeks: buckets }),
  },
  month: {
    method: 'monthlyHistory',
    kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.MonthlyHistory,
    counts: (buckets) => ({ numberOfMonths: buckets }),
  },
};

/** What `requestParameters` builds: the page's filters plus this module's four source options. */
export type SourceStatsRequestParameters = FiltersPayload & {
  includeSources: boolean;
  groupBy: 'domain' | 'hostname';
  entityTermKey?: string[];
  sourceHosts?: string[];
};

function requestParameters(query: SourceStatsQuery): SourceStatsRequestParameters {
  return {
    ...query.filters,
    // Opt-in, so a caller that does not want the block pays neither the aggregation nor the payload.
    includeSources: true,
    // The server's own vocabulary: `hostname` is the precise per-host view, `domain` the default.
    groupBy: query.groupBy === 'host' ? 'hostname' : 'domain',
    ...(query.entityTermKey ? { entityTermKey: [query.entityTermKey] } : {}),
  };
}

/**
 * The ranked sources for the current window, or undefined when this deployment does not serve them.
 *
 * `undefined` and `[]` are kept apart deliberately: an empty array is the measurement "these
 * answers cited nothing", while undefined means the question was not answered at all — which is
 * what an older Sync Core does, since its generated response type has no `sources` field to map.
 *
 * The narrowing to the local `SourceStatsTotal` is the module's adapter, not a version stopgap: the
 * generated response types every per-source figure as required, and this module renders a missing
 * figure as absence rather than as a zero, so it reads them as optional on purpose.
 */
export async function fetchSourceStatsTotal(
  api: SourceStatsApi,
  query: SourceStatsQuery,
  signal: AbortSignal
): Promise<SourceStatsTotal | undefined> {
  const parameters = requestParameters(query);
  const response = await scheduleStatsRequest({
    key: statsRequestKey({ method: 'total', parameters: [parameters] }),
    priority: StatsRequestPriority.Sources,
    signal,
    run: () => api.governance.promptMonitoringStats.total(parameters),
    combinable: promptMonitoringAspect(api, { kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.Total, filters: parameters }),
  });

  if (!response || !Array.isArray(response.sources)) {
    return undefined;
  }

  return response;
}

/**
 * The per-bucket series for a bounded set of sources — the leaderboard rows currently on screen.
 *
 * Since rest-client 4.135.1 all three granularities declare `sources[]` on their bucket AND map it
 * in `FromJSON`, so a current deployment lights the Trend column up. Both halves matter: a generated
 * client strips response fields it does not map, so a declared-but-unmapped field would arrive as
 * undefined however faithfully the server answered.
 *
 * The undefined return therefore no longer describes "every deployment" — but it stays, because a
 * Sync Core older than the release that added the series still answers without them, and connected
 * deployments upgrade on their own schedule. The caller reads undefined as "no trends to show" and
 * drops the column rather than filling it with dashes.
 *
 * Bounded because the series exist to draw the sparklines beside those rows; asking for the whole
 * long tail would cost a great deal to render nothing anybody looks at.
 */
export async function fetchSourceStatsHistory(
  api: SourceStatsApi,
  query: SourceStatsQuery,
  granularity: SourceHistoryGranularity,
  buckets: number,
  sourceHosts: readonly string[],
  signal: AbortSignal
): Promise<SourceHistoryBucket[] | undefined> {
  const parameters = { ...requestParameters(query), sourceHosts: [...sourceHosts] };
  const history = HISTORY_OF_GRANULARITY[granularity];

  const response = await scheduleStatsRequest({
    key: statsRequestKey({ method: history.method, parameters: [buckets, parameters] }),
    priority: StatsRequestPriority.SourceTrends,
    signal,
    run: () => api.governance.promptMonitoringStats[history.method](buckets, parameters),
    combinable: promptMonitoringAspect(api, { kind: history.kind, filters: parameters, counts: history.counts(buckets) }),
  });

  if (!Array.isArray(response) || !response.some((bucket) => Array.isArray(bucket?.sources))) {
    return undefined;
  }

  return response;
}
